// umlaut-check: skip — "ErklaerungAbschnitt"/"Erklaerung" sind Komponenten-
// bzw. Ordnernamen (technische Bezeichner), die sichtbaren Texte tragen
// Umlaute korrekt.

import React, {useCallback, useState} from "react";
import {Container, Spinner} from "react-bootstrap";
import HeaderWithHelp from "../../Components/SalesAnalysis/HeaderWithHelp.jsx";
import PurposeBox from "../../Components/Cosmos/PurposeBox.jsx";
import DatenquellenTabelle from "../../Components/Cosmos/DatenquellenTabelle.jsx";
import EinstellungenTabelle from "../../Components/Cosmos/EinstellungenTabelle.jsx";
import AusschluesseTabelle from "../../Components/Cosmos/AusschluesseTabelle.jsx";
import LiefergelegenheitenListe from "../../Components/Cosmos/LiefergelegenheitenListe.jsx";
import BestandslisteZuordnung from "../../Components/Cosmos/BestandslisteZuordnung.jsx";
import UnverzerrteNachfrage from "../../Components/Cosmos/UnverzerrteNachfrage.jsx";
import ErklaerungAbschnitt from "../../Components/Cosmos/Erklaerung/ErklaerungAbschnitt.jsx";
import useAnalysisData from "../../Components/SalesAnalysis/useAnalysisData.js";
import {hasAbility} from "../../Components/SalesAnalysis/format.js";

/**
 * Seite "Quellen und Berechnungslogik" (PLAN.md Abschnitt 4.6/5, AP3;
 * umbenannt und um die Erklärung "So rechnet der Bereich" ergänzt, Auftrag
 * S7, 22.09.2026): oben acht präsentationstaugliche Folien, die die
 * Berechnung in verständlicher Sprache erzählen, darunter unverändert die
 * Kennzahlen-Tabelle, gepflegte Annahmen, Ausschlüsse, Liefergelegenheiten
 * und die unverzerrte B2B-Nachfrage. Jede Pflegeänderung lädt die Seite über
 * `neuLaden` nach, ohne die Seite selbst neu zu laden.
 */
export default function CosmosDatenquellen() {
    const [ladeSchluessel, setLadeSchluessel] = useState(0);
    const {data, loading, error} = useAnalysisData("/cosmos/daten/datenquellen", {_r: ladeSchluessel || undefined});
    const neuLaden = useCallback(() => setLadeSchluessel((n) => n + 1), []);
    const canParameters = hasAbility(data?.meta, "canParameters");

    return (
        <Container fluid className="pt-2 pb-4">
            <PurposeBox storageKey="datenquellen">
                Erklärt in acht Folien, wie sich die Berechnungen des Bereichs zusammensetzen, und listet darunter je
                Kennzahl, aus welcher Quelle sie stammt, wie alt der Stand ist, wie oft sie erneuert wird, wie
                verlässlich sie ist und wie sie gerechnet wird – die Antwort auf die Frage, worauf man gerade schaut.
            </PurposeBox>
            <h1 className="h4 mb-2">
                <HeaderWithHelp help="Herkunft, Alter, Aktualisierung, Verlässlichkeit und Rechenweg jeder Kennzahl im Bereich, dazu eine verständliche Erklärung der Berechnung.">
                    Quellen und Berechnungslogik
                </HeaderWithHelp>
            </h1>

            {error && <p className="text-danger">{error}</p>}

            {!data && loading && (
                <div className="d-flex justify-content-center py-5">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Wird geladen…</span>
                    </Spinner>
                </div>
            )}

            {data && (
                <>
                    <div className="mb-4">
                        <h2 className="h5 mb-2">
                            <HeaderWithHelp help="Acht Folien wie in einer Präsentation, mit Grafiken und echten Beispielzahlen – gedacht, um sie im Meeting Schritt für Schritt zu zeigen.">
                                So rechnet der Bereich
                            </HeaderWithHelp>
                        </h2>
                        <ErklaerungAbschnitt einstellungen={data.einstellungen} kennzahlen={data.kennzahlen} />
                    </div>

                    <h2 className="h5 mt-4 mb-2">
                        <HeaderWithHelp help="Dieselben Quellen wie in der Erklärung oben, hier als vollständige, gepflegte Liste – Kennzahlen, Annahmen, Ausschlüsse und Liefergelegenheiten.">
                            Alle Quellen im Detail
                        </HeaderWithHelp>
                    </h2>

                    <DatenquellenTabelle kennzahlen={data.kennzahlen} />

                    <BestandslisteZuordnung bestandsliste={data.bestandsliste} />

                    <h2 className="h5 mt-4">
                        <HeaderWithHelp help="Annahmen, die als Zahl im Bereich stecken, statt als Konstante im Programmcode – hier sichtbar und mit Begründung nachvollziehbar.">
                            Annahmen und Stellschrauben
                        </HeaderWithHelp>
                    </h2>
                    <EinstellungenTabelle einstellungen={data.einstellungen} canParameters={canParameters} onGeaendert={neuLaden} />

                    <h2 className="h5 mt-4">
                        <HeaderWithHelp help="Lohnfertiger, Verrechnungskonten und ähnliche Sonderfälle, die vor jeder Bedarfsrechnung herausgerechnet werden.">
                            Ausschlüsse von der Bedarfsrechnung
                        </HeaderWithHelp>
                    </h2>
                    <AusschluesseTabelle ausschluesse={data.ausschluesse} canParameters={canParameters} onGeaendert={neuLaden} />

                    <h2 className="h5 mt-4">
                        <HeaderWithHelp help="Die Kalenderwochen, in denen Cosmos tatsächlich anliefert – Grundlage für Ausfallwoche, Reichweite und die vier Marken im Zeitstrahl.">
                            Liefergelegenheiten
                        </HeaderWithHelp>
                    </h2>
                    <LiefergelegenheitenListe liefergelegenheiten={data.liefergelegenheiten} canParameters={canParameters} onGeaendert={neuLaden} />

                    <h2 className="h5 mt-4">
                        <HeaderWithHelp help="Nachfrage aus dem B2B-Shop, die weder Bestellung noch Faktura wurde – Suchen ohne Treffer und stehengebliebene Warenkörbe.">
                            Unverzerrte Nachfrage aus dem B2B-Shop
                        </HeaderWithHelp>
                    </h2>
                    <UnverzerrteNachfrage sucheOhneTreffer={data.b2b_suchen_ohne_treffer} warenkoerbeTop={data.b2b_warenkoerbe_top} />
                </>
            )}
        </Container>
    );
}
